import { useState, type CSSProperties, type ReactNode } from "react";
import { motion } from "motion/react";
import { ArrowUp } from "@phosphor-icons/react";
import { useLocation } from "wouter";
import type { Funnel } from "../../shared/types.ts";
import { Button, Highlight, Logo, SOURCES_SHOWN, Sankey, SourceLogo, cn, ease, rise, spring, staggerParent } from "../ui";
import { CtaBand, Footer, Pricing, Proof } from "./Closing";
import { Faq } from "./Faq";
import { Laptop, PhoneSwipe, TossedCard } from "./mocks";
import { Item, Reveal } from "./Reveal";
import { AnswerForm, LiveStatus, OnSite, RankedList, Sending, Sources } from "./stages";
import { Bar } from "./shapes";
import { fade, h2, script, scriptHref, wrap } from "./styles";

type LandingProps = { onStart: () => void; onLogIn: () => void };

function Nav({ onStart, onLogIn }: LandingProps) {
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-page/85 pt-safe backdrop-blur-xl">
      <div className="flex h-16 items-stretch">
        <a href="#top" className="flex items-center border-r border-line px-4 sm:px-6" aria-label="Headcount home">
          <Logo size={24} />
        </a>
        <nav aria-label="Sections" className="hidden items-stretch md:flex">
          {[
            ["#how", "How it works"],
            ["#pricing", "Pricing"],
            ["#faq", "FAQ"],
            ["/jobs", "Jobs"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="flex items-center border-r border-line px-6 text-[15px] font-medium text-ink-soft transition-[background-color,color] duration-150 hover:bg-sunk hover:text-ink"
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 border-l border-line px-3 sm:px-5">
          <Button variant="ghost" size="sm" onClick={onLogIn}>
            Log in
          </Button>
          <Button size="sm" onClick={onStart}>
            Start applying
          </Button>
        </div>
      </div>
    </header>
  );
}

/** Asterisks from the mark, spun in either side of the headline. */
function Planes() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 hidden lg:block">
      <Bar tone="blue" delay={0.45} className="h-[220px] w-[76px]" style={{ left: "calc(50% + 524px)", top: 120 }} />
      <Bar tone="orange" delay={0.55} className="h-[200px] w-[76px]" style={{ left: "calc(50% + 612px)", top: 72 }} />
      <Bar tone="tint" flip delay={0.65} className="h-[210px] w-[72px]" style={{ left: "calc(50% - 640px)", top: 300 }} />
      <Bar tone="orange" flip delay={0.75} className="h-[120px] w-[40px]" style={{ left: "calc(50% - 552px)", top: 380 }} />
    </div>
  );
}

const filters = [
  { key: "remote", label: "Remote" },
  { key: "auto", label: "Auto-apply" },
  { key: "pay", label: "$150k+" },
];

/** The hero's search box. Submitting what was typed opens those jobs on the public jobs page; submitting nothing opens sign-up. */
function HeroSearch({ onStart }: { onStart: () => void }) {
  const [, navigate] = useLocation();
  const [on, setOn] = useState<Record<string, boolean>>({ remote: true });
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        const q = String(new FormData(e.currentTarget).get("q") ?? "").trim();
        if (q) navigate(`/jobs?${new URLSearchParams({ q })}`);
        else onStart();
      }}
      className="w-full max-w-[760px] rounded-card bg-surface p-2.5 text-left shadow-raised transition-[box-shadow] duration-200 focus-within:shadow-[0_0_0_2px_var(--primary),0_12px_32px_-12px_rgba(22,30,48,0.22)]"
    >
      <label className="sr-only" htmlFor="hero-search">
        Roles Headcount should apply to
      </label>
      <input
        id="hero-search"
        name="q"
        type="search"
        autoComplete="off"
        placeholder="What should Headcount apply to?"
        className="h-14 w-full bg-transparent px-3 text-[18px] text-ink outline-none placeholder:text-muted"
      />
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none]">
          {filters.map((f) => (
            <motion.button
              key={f.key}
              type="button"
              aria-pressed={!!on[f.key]}
              onClick={() => setOn((o) => ({ ...o, [f.key]: !o[f.key] }))}
              whileTap={{ scale: 0.94 }}
              transition={spring}
              className={cn(
                "inline-flex h-9 shrink-0 items-center rounded-control px-3.5 text-sm font-semibold transition-[background-color,color,box-shadow] duration-150",
                on[f.key] ? "bg-primary-soft text-primary-ink ring-1 ring-inset ring-primary/35" : "text-ink-soft ring-1 ring-inset ring-line-strong hover:bg-sunk hover:text-ink",
              )}
            >
              {f.label}
            </motion.button>
          ))}
        </div>
        <motion.button
          type="submit"
          aria-label="Search"
          whileTap={{ scale: 0.9 }}
          transition={spring}
          className="grid size-11 shrink-0 place-items-center rounded-control bg-primary text-on-primary shadow-brand transition-[background-color] duration-150 hover:bg-primary-hover"
        >
          <ArrowUp size={20} weight="bold" aria-hidden />
        </motion.button>
      </div>
    </form>
  );
}

function Hero({ onStart }: { onStart: () => void }) {
  return (
    <section id="top" className="relative overflow-hidden">
      <div aria-hidden className="grid-lines pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_75%_60%_at_50%_22%,black_30%,transparent_80%)]" />
      <Planes />
      <motion.div
        className={cn(wrap, "relative flex flex-col items-center pt-16 text-center md:pt-28")}
        variants={staggerParent(0.09, 0.05)}
        initial="hidden"
        animate="show"
      >
        <h1 className="font-display text-[clamp(2.75rem,6vw,5.25rem)] font-bold leading-[1] tracking-[-0.05em] text-ink">
          <motion.span variants={rise} className="inline-block">
            Apply to jobs
          </motion.span>{" "}
          <motion.span variants={rise} className="inline-block text-primary">
            on autopilot.
          </motion.span>
        </h1>
        <motion.p variants={rise} className="mt-6 max-w-[34rem] text-[18px] leading-relaxed text-ink-soft md:text-[20px]">
          Upload your resume. Headcount finds the jobs that fit you and applies to every one.
        </motion.p>
        <motion.div variants={rise} className="mt-10 flex w-full flex-col items-center gap-4">
          <HeroSearch onStart={onStart} />
          <p className="text-[15px] text-muted">
            <span className="font-semibold text-ink tabular-nums">86,000+</span> open roles at <span className="font-semibold text-ink tabular-nums">217</span> companies.
          </p>
        </motion.div>
      </motion.div>
      <div className={cn(wrap, "relative mt-20 pb-6 md:mt-28 md:pb-10")} aria-hidden>
        <motion.p
          initial={{ opacity: 0, y: 12, rotate: -10 }}
          animate={{ opacity: 1, y: 0, rotate: -6 }}
          transition={{ type: "spring", stiffness: 120, damping: 14, delay: 1.1 }}
          className={cn(script, "absolute -top-14 right-8 z-20 hidden items-end gap-2 text-[30px] leading-[0.95] text-primary-ink md:flex lg:right-24")}
        >
          <span className="text-right">
            Jobs move fast.
            <br />
            So do you.
          </span>
          <svg viewBox="0 0 40 48" className="mb-[-18px] h-12 w-10 text-accent" fill="none" aria-hidden>
            <path d="M6 4c14 4 22 14 22 34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="m20 32 8 8 6-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.p>
        {/* Product frame: swap these CSS mocks for real screenshots once the app is live. */}
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 60, damping: 16, delay: 0.35 }}
          className="band relative h-[540px] overflow-hidden rounded-card ring-1 ring-line md:h-[640px]"
        >
          <Bar tone="blue" delay={0.6} className="-bottom-24 -left-10 h-[520px] w-[220px] md:h-[600px] md:w-[300px]" />
          <Bar tone="orange" flip delay={0.7} className="-right-10 -top-24 h-[360px] w-[150px] md:h-[440px] md:w-[200px]" />
          <motion.div
            initial={{ opacity: 0, y: 60, rotateX: 14 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ type: "spring", stiffness: 70, damping: 18, delay: 0.5 }}
            style={{ transformPerspective: 1600 }}
            className="absolute right-12 top-14 hidden w-[74%] md:block"
          >
            <Laptop />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 120, rotate: 8 }}
            animate={{ opacity: 1, y: 0, rotate: -4 }}
            transition={{ type: "spring", stiffness: 80, damping: 14, delay: 0.7 }}
            className="absolute left-1/2 top-10 z-10 -translate-x-1/2 md:left-12 md:top-24 md:translate-x-0"
          >
            <PhoneSwipe />
          </motion.div>
          <TossedCard
            company="Stripe"
            title="Product designer, payments"
            verdict="apply"
            className="absolute bottom-16 right-14 z-10 hidden rotate-[7deg] lg:block"
          />
        </motion.div>
      </div>
    </section>
  );
}

/** Desktop wordmark heights in px, tuned by eye so each vendor's logotype reads the same size. Phones get 85%. */
const wordHeights: Record<string, number> = {
  greenhouse: 30,
  lever: 22,
  ashby: 28,
  workable: 26,
  recruitee: 34,
  bamboohr: 24,
  jazzhr: 23,
  workday: 44,
  smartrecruiters: 22,
};

function Marquee() {
  return (
    <section className="relative z-0 mt-10 border-y border-line bg-surface py-6 md:mt-16">
      <div className="flex items-center gap-6">
        <p className="hidden shrink-0 border-r border-line pl-6 pr-6 text-[15px] font-semibold text-muted md:block">Jobs from</p>
        <div className="relative min-w-0 flex-1 overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
          <div className="flex w-max animate-marquee items-center">
            {[0, 1].map((k) => (
              <ul key={k} className="flex items-center" aria-hidden={k === 1} aria-label={k === 0 ? "Job sources" : undefined}>
                {SOURCES_SHOWN.map((s) => (
                  <li key={s} className="flex items-center pr-16">
                    <span className="contents" style={{ "--h": `${wordHeights[s] ?? 26}px` } as CSSProperties}>
                      <SourceLogo source={s} variant="wordmark" className="h-[calc(var(--h)*0.85)] outline-none md:h-(--h)" />
                    </span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Urgency({ onStart }: { onStart: () => void }) {
  return (
    <Reveal as="section" className={cn(wrap, "flex flex-col items-center py-24 text-center md:py-36")}>
      <Item as="h2" className={cn(h2, "max-w-[14ch]")}>
        Someone is hiring for your next job <span className={fade}>right now.</span>
      </Item>
      <Item>
        <Button size="lg" className="mt-9" onClick={onStart}>
          Start applying
        </Button>
      </Item>
    </Reveal>
  );
}

const steps = ["Add your resume", "Swipe the roles you want", "Headcount sends them"];

function How() {
  return (
    <section id="how" className={cn(wrap, "pb-24 md:pb-36")}>
      <Reveal>
        <Item as="h2" className={cn(h2, "mx-auto max-w-[19ch] text-center")}>
          Add your resume. <span className={fade}>We’ll take it from there.</span>
        </Item>
      </Reveal>
      <Reveal as="ol" gap={0.14} className="relative mx-auto mt-14 grid max-w-[960px] gap-8 md:mt-20 md:grid-cols-3 md:gap-4">
        <motion.span
          aria-hidden
          className="absolute left-7 top-7 bottom-7 w-0.5 origin-top bg-primary-tint md:inset-x-[16.6%] md:bottom-auto md:left-[16.6%] md:h-0.5 md:w-auto md:origin-left"
          variants={{ hidden: { scaleX: 0, scaleY: 0 }, show: { scaleX: 1, scaleY: 1, transition: { duration: 1.1, ease } } }}
        />
        {steps.map((s, i) => (
          <Item as="li" key={s} className="group relative flex items-center gap-5 md:flex-col md:text-center">
            <span
              className={cn(
                "grid size-14 shrink-0 place-items-center rounded-card font-display text-[22px] font-extrabold tabular-nums transition-transform duration-300 ease-spring group-hover:-skew-x-[10deg] group-hover:scale-110",
                i === 0 ? "bg-primary-soft text-primary-ink shadow-card" : i === 1 ? "bg-cream text-cream-ink shadow-card" : "bg-primary text-on-primary shadow-brand",
              )}
            >
              {i + 1}
            </span>
            <span className="font-display text-[22px] font-bold tracking-[-0.035em] text-ink">{s}</span>
          </Item>
        ))}
      </Reveal>
    </section>
  );
}

type Tone = "brand" | "cream" | "gray";

const tones: Record<Tone, string> = {
  brand: "bg-primary-soft",
  cream: "bg-cream",
  gray: "bg-sunk",
};

function BigCard({ tone, title, children }: { tone: Tone; title: string; children: ReactNode }) {
  return (
    <Item className="h-full">
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className={cn("relative flex h-full flex-col overflow-hidden rounded-card p-6 text-ink sm:p-8 md:min-h-[460px]", tones[tone])}
      >
        <h3 className="relative font-display text-[26px] font-bold leading-[1.1] tracking-[-0.035em]">{title}</h3>
        <div className="relative mt-auto pt-10">{children}</div>
      </motion.div>
    </Item>
  );
}

const sample: Funnel = { swiped: 214, sent: 196, waiting: 12, failed: 6, noReply: 118, rejected: 47, interviews: 31, offers: 4, noOffer: 9 };

function Charted() {
  return (
    <section className={cn(wrap, "pb-24 md:pb-36")}>
      <Reveal>
        <Item as="h2" className={cn(h2, "max-w-[16ch]")}>
          Watch the <Highlight>interviews</Highlight> <span className={fade}>add up.</span>
        </Item>
      </Reveal>
      <Reveal className="mt-10 md:mt-14">
        <Item className="rounded-card bg-cream p-4 sm:p-8 md:p-10">
          <div className="flex items-center justify-between gap-4">
            <h3 className="font-display text-[26px] font-bold leading-[1.1] tracking-[-0.035em] text-ink">Share your Sankey.</h3>
            <span aria-hidden className="inline-flex h-10 shrink-0 items-center rounded-control bg-primary px-4 text-[15px] font-semibold text-on-primary shadow-brand">
              Share
            </span>
          </div>
          <div className="mt-8 rounded-card bg-surface p-3 shadow-card sm:p-6">
            <Sankey funnel={sample} />
          </div>
        </Item>
      </Reveal>
    </section>
  );
}

function Stage({ headline, cards }: { headline: ReactNode; cards: ReactNode }) {
  return (
    <section className={cn(wrap, "pb-24 md:pb-36")}>
      <Reveal>
        <Item as="h2" className={cn(h2, "max-w-[16ch]")}>
          {headline}
        </Item>
      </Reveal>
      <Reveal gap={0.12} className="mt-10 grid grid-cols-1 gap-4 md:mt-14 md:grid-cols-2">
        {cards}
      </Reveal>
    </section>
  );
}

export function Landing({ onStart, onLogIn }: LandingProps) {
  return (
    <div className="min-h-dvh overflow-x-clip bg-page pl-safe pr-safe">
      <link rel="stylesheet" href={scriptHref} precedence="default" />
      <Nav onStart={onStart} onLogIn={onLogIn} />
      <main>
        <Hero onStart={onStart} />
        <Marquee />
        <Urgency onStart={onStart} />
        <How />
        <Stage
          headline={
            <>
              Headcount <Highlight>finds</Highlight> <span className={fade}>the roles worth your time.</span>
            </>
          }
          cards={
            <>
              <BigCard tone="brand" title="Best match first.">
                <RankedList />
              </BigCard>
              <BigCard tone="cream" title="Company sites get 68% more interviews.">
                <Sources />
              </BigCard>
            </>
          }
        />
        <Stage
          headline={
            <>
              Swipe right. <span className={fade}>It fills</span> <Highlight>every form</Highlight> <span className={fade}>for you.</span>
            </>
          }
          cards={
            <>
              <BigCard tone="gray" title="Every question answered.">
                <AnswerForm />
              </BigCard>
              <BigCard tone="brand" title="Apply and it’s on its way.">
                <Sending />
              </BigCard>
            </>
          }
        />
        <Stage
          headline={
            <>
              See <Highlight>every</Highlight> <span className={fade}>application go out.</span>
            </>
          }
          cards={
            <>
              <BigCard tone="cream" title="Live status on every one.">
                <LiveStatus />
              </BigCard>
              <BigCard tone="gray" title="Workday opens on the company site.">
                <OnSite />
              </BigCard>
            </>
          }
        />
        <Charted />
        <Proof />
        <Pricing onStart={onStart} />
        <section id="faq" className={cn(wrap, "pb-24 md:pb-36")}>
          <div className="mx-auto max-w-[760px]">
            <h2 className={cn(h2, "mb-8 md:mb-12")}>FAQ</h2>
            <Faq />
          </div>
        </section>
        <CtaBand onStart={onStart} />
      </main>
      <Footer />
    </div>
  );
}
